/* eslint-disable @typescript-eslint/no-magic-numbers */
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InstanceProps, create } from 'react-modal-promise';
import { useApolloClient } from '@apollo/client';
import { Button, Col, Form, Input, Modal, Row, notification } from 'antd';
import { UploadType, useEmployee, useUploadDeleteUri } from 'hooks';
import { EpCertificate } from 'types/graphql-types';
import { CertificateImage } from './certificate-image';
import styles from './styles.less';

type CertificateToSave = Pick<EpCertificate, 'certificate'> & {
  image?: Blob | null;
};
type CertificatesToSave = { [key: string]: CertificateToSave };

const EditCertificatesModal: FC<
  InstanceProps<React.MouseEvent<HTMLElement>>
> = ({ isOpen, onResolve }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { employee } = useEmployee();

  const client = useApolloClient();

  const certificates = employee?.certificates?.slice().reverse() || [];

  const [certificatesToSave, setCertificatesToSave] =
    useState<CertificatesToSave>({});

  const initialValues = certificates.reduce(
    (acc, cert) => ({
      ...acc,
      [`cert-${cert.id}`]: cert.certificate,
    }),
    {} as Record<string, string | null | undefined>,
  );

  const onCertificateChange = (
    updatedCertificateId: string,
    certificate: { title?: string; image?: Blob | null },
  ): void => {
    setCertificatesToSave(
      (changedCertificates: CertificatesToSave): CertificatesToSave => {
        const updatedCertificate =
          changedCertificates[updatedCertificateId] || {};

        if (certificate.image !== undefined) {
          updatedCertificate.image = certificate.image;
        }

        if (certificate.title !== undefined) {
          updatedCertificate.certificate = certificate.title;
        }

        changedCertificates[updatedCertificateId] = updatedCertificate;

        return changedCertificates;
      },
    );
  };

  const onClose = (): void => {
    Modal.confirm({
      content: t('Changes you made so far will not be saved'),
      okText: t('Yes, quit'),
      onOk: () => onResolve(),
      title: t('Quit editing?'),
    });
  };

  const { uploadResource: uploadCertificates, loading } = useUploadDeleteUri(
    UploadType.CERTIFICATES,
  );

  const onSave = (): void => {
    const updatedCertificates = Object.entries(certificatesToSave);

    const updatedImagesCertificats = updatedCertificates
      .filter(([_key, cert]) => cert.image)
      .map(([id, { image }]) => ({ id, image }));
    const updatedTitlesCertificats = updatedCertificates
      .filter(([_key, cert]) => cert.certificate)
      .map(([id, { certificate }]) => ({
        certificate,
        id,
      }));
    const deletedImagesCertificats = updatedCertificates
      .filter(([_key, cert]) => cert.image === null)
      .map(([id, _cert]) => id);

    const formData = new FormData();

    if (updatedImagesCertificats?.length) {
      updatedImagesCertificats.forEach(({ image, id }) => {
        if (!image || !id) {
          throw new Error('Updated image data should be provided!');
        }

        formData.append('images[]', image, id.toString());
      });
    }

    if (updatedTitlesCertificats?.length) {
      formData.append(
        'updatedTitles',
        JSON.stringify(updatedTitlesCertificats),
      );
    }

    if (deletedImagesCertificats?.length) {
      formData.append(
        'deletedImages',
        JSON.stringify(deletedImagesCertificats),
      );
    }

    uploadCertificates(formData)
      .then(() => {
        notification.success({
          message: 'Certificates have been saved!',
        });
        onResolve();
        void client.refetchQueries({ include: ['GetEmployee'] });
      })
      .catch(() => {
        notification.error({
          message: 'Something has gone wrong. Try again!',
        });
      });
  };

  return (
    <Modal
      open={isOpen}
      centered
      okText={t('Save')}
      cancelText={t('Cancel')}
      title={t('Edit certificate')}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} loading={loading}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          form="edit-certificate-form"
          htmlType="submit"
          loading={loading}
        >
          Save
        </Button>,
      ]}
    >
      <Form
        form={form}
        name="edit-certificate-form"
        layout="vertical"
        initialValues={initialValues}
        preserve={false}
        onFinish={onSave}
      >
        <Row
          align="middle"
          justify="center"
          gutter={[16, 24]}
          className={styles['row']}
        >
          {certificates.map((cert) => {
            const image = cert.image || '';

            return (
              <React.Fragment key={`cert-${cert.id}`}>
                <Col span={8}>
                  <CertificateImage
                    src={image}
                    onChangeFile={(file) =>
                      onCertificateChange(cert.id, {
                        image: file,
                      })
                    }
                  />
                </Col>
                <Col span={16}>
                  <Form.Item
                    label={'Certificate title'}
                    name={`cert-${cert.id}`}
                    rules={[
                      {
                        message: 'Title is mandatory',
                        required: true,
                      },
                      {
                        message: 'Title is mandatory',
                        min: 1,
                      },
                      {
                        max: 100,
                        message: 'Title is too long',
                      },
                    ]}
                    requiredMark={'optional'}
                  >
                    <Input
                      placeholder="Enter certificate title"
                      maxLength={100}
                      onChange={(e) =>
                        onCertificateChange(cert.id, {
                          title: e.target.value,
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              </React.Fragment>
            );
          })}
        </Row>
      </Form>
    </Modal>
  );
};

export const editCertificatesModal = create(EditCertificatesModal);
